import { useContext, useState, useMemo, useEffect } from "react";
import "./UpdatePrices.scss";
import { Button } from "../../../Components/Button";
import { Title, Text } from "../../../Components/Typography";
import { LogoSection } from "../../../Components/LogoSection";
import useResponsive from "../../../hooks/useResponsive";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../Router";
import { useLogin } from "../../../Contexts/LoginAdminContext";
import { usePreciosCopias } from "../../../hooks/usePreciosCopias";
import useApi from "../../../hooks/useApi";
import { Table } from "../../../Components/Table";
import { LoadingSpinner } from "../../../Components/Loading";
import { EditPriceModal } from "./EditPriceModal";
import { BulkEditModal } from "./BulkEditModal";
import { PriceFilters } from "./PriceFilters";
import { PrecioCopia } from "../../../hooks/usePreciosCopias";
import { toast } from "react-toastify";

export const AdminUpdatePrices = () => {
  const { isDesktopOrLaptop } = useResponsive();
  const navigate = useNavigate();
  const { email } = useLogin();
  const { precios, loading, error, refreshPrecios } = usePreciosCopias();
  const { updatePrecioCopia } = useApi();
  
  // Estados para el modal de edición
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPrecio, setSelectedPrecio] = useState<PrecioCopia | null>(null);
  const [updatingPrice, setUpdatingPrice] = useState(false);
  
  // Estados para edición masiva
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [selectedPrecios, setSelectedPrecios] = useState<PrecioCopia[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    selectedColors: [] as string[],
    selectedPapers: [] as string[],
    selectedSizes: [] as string[],
    selectedFaz: [] as string[],
    searchTerm: ''
  });
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // 10 registros por página

  const handleGoBack = () => {
    navigate(routes.adminOrders.path);
  };

  const handleRefresh = () => {
    refreshPrecios();
    setCurrentPage(1); // Resetear a la primera página al refrescar
  };

  const handleEditPrice = (precio: PrecioCopia) => {
    setSelectedPrecio(precio);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedPrecio(null);
  };

  const handleSavePrice = async (id: number, newPrice: number) => {
    setUpdatingPrice(true);
    
    try {
      const response = await updatePrecioCopia(id, newPrice);
      
      if (response && response.success) {
        toast.success('Precio actualizado correctamente');
        // Refrescar la lista de precios
        await refreshPrecios();
      } else {
        toast.error(response?.error || 'Error al actualizar el precio');
        throw new Error(response?.error || 'Error al actualizar el precio');
      }
    } catch (err) {
      toast.error('Error al actualizar el precio');
      throw err;
    } finally {
      setUpdatingPrice(false);
    }
  };

  // Función para filtrar precios
  const filteredPrecios = useMemo(() => {
    return precios.filter(precio => {
      // Filtro por búsqueda
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch = 
          precio.color_name.toLowerCase().includes(searchLower) ||
          precio.papel_name.toLowerCase().includes(searchLower) ||
          precio.hoja_name.toLowerCase().includes(searchLower) ||
          precio.faz_name.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Filtro por color
      if (filters.selectedColors.length > 0 && !filters.selectedColors.includes(precio.color_name)) {
        return false;
      }

      // Filtro por papel
      if (filters.selectedPapers.length > 0 && !filters.selectedPapers.includes(precio.papel_name)) {
        return false;
      }

      // Filtro por tamaño
      if (filters.selectedSizes.length > 0 && !filters.selectedSizes.includes(precio.hoja_name)) {
        return false;
      }

      // Filtro por faz
      if (filters.selectedFaz.length > 0 && !filters.selectedFaz.includes(precio.faz_name)) {
        return false;
      }

      return true;
    });
  }, [precios, filters]);

  // Función para manejar edición masiva
  const handleBulkEdit = async (updates: { id: number; newPrice: number }[]) => {
    setBulkUpdating(true);
    
    try {
      // Actualizar cada precio individualmente
      for (const update of updates) {
        const response = await updatePrecioCopia(update.id, update.newPrice);
        if (!response || !response.success) {
          throw new Error(`Error al actualizar precio ID ${update.id}`);
        }
      }
      
      toast.success(`${updates.length} precios actualizados correctamente`);
      await refreshPrecios();
      setSelectedPrecios([]);
    } catch (err) {
      toast.error('Error al actualizar algunos precios');
      throw err;
    } finally {
      setBulkUpdating(false);
    }
  };

  // Función para limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      selectedColors: [],
      selectedPapers: [],
      selectedSizes: [],
      selectedFaz: [],
      searchTerm: ''
    });
    setCurrentPage(1);
  };

  // Función para manejar selección de precios
  const handlePrecioSelection = (precio: PrecioCopia, selected: boolean) => {
    if (selected) {
      setSelectedPrecios(prev => [...prev, precio]);
    } else {
      setSelectedPrecios(prev => prev.filter(p => p.id !== precio.id));
      // Si se deselecciona un precio, también cancelar su edición si está en modo edición
      if (editingPrecios[precio.id]) {
        handleCancelEdit(precio.id);
      }
    }
  };

  // Función para seleccionar todos los precios filtrados
  const handleSelectAll = () => {
    setSelectedPrecios(filteredPrecios);
    // Si ya hay precios en edición, mantenerlos
    // Si no hay ninguno en edición, no hacer nada
  };

  // Función para deseleccionar todos
  const handleDeselectAll = () => {
    setSelectedPrecios([]);
    // Limpiar también todos los estados de edición
    setEditingPrecios({});
    setEditingValues({});
  };

  // Función para editar todos los precios seleccionados
  const handleEditSelected = () => {
    selectedPrecios.forEach(precio => {
      setEditingPrecios(prev => ({ ...prev, [precio.id]: true }));
      setEditingValues(prev => ({ ...prev, [precio.id]: precio.precio.toString() }));
    });
  };

  // Función para guardar todos los precios en edición
  const handleSaveAllEditing = async () => {
    const editingPreciosList = currentPrecios.filter(precio => editingPrecios[precio.id]);
    
    if (editingPreciosList.length === 0) {
      toast.info('No hay precios en edición para guardar');
      return;
    }

    // Filtrar solo los precios que realmente han cambiado
    const changedPrecios = editingPreciosList.filter(precio => {
      const newPrice = parseFloat(editingValues[precio.id]);
      return !isNaN(newPrice) && newPrice >= 0 && newPrice !== precio.precio;
    });

    if (changedPrecios.length === 0) {
      toast.info('No hay cambios para guardar');
      // Limpiar todos los estados de edición
      setEditingPrecios({});
      setEditingValues({});
      return;
    }

    setBulkUpdating(true);
    
    try {
      for (const precio of changedPrecios) {
        const newPrice = parseFloat(editingValues[precio.id]);
        await handleSavePrice(precio.id, newPrice);
      }
      
      // Limpiar todos los estados de edición
      setEditingPrecios({});
      setEditingValues({});
      toast.success(`${changedPrecios.length} precios actualizados correctamente`);
    } catch (error) {
      console.error('Error al guardar precios:', error);
      toast.error('Error al guardar algunos precios');
    } finally {
      setBulkUpdating(false);
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toLocaleString('es-AR')}`;
  };

  const formatDimensions = (width: number, height: number) => {
    return `${width} × ${height} mm`;
  };

  // Función para verificar si hay precios con cambios reales
  const hasRealChanges = () => {
    const editingPreciosList = currentPrecios.filter(precio => editingPrecios[precio.id]);
    return editingPreciosList.some(precio => {
      const newPrice = parseFloat(editingValues[precio.id]);
      return !isNaN(newPrice) && newPrice >= 0 && newPrice !== precio.precio;
    });
  };

  // Función para limpiar estados de edición de precios no seleccionados
  const cleanupEditingStates = () => {
    const selectedIds = selectedPrecios.map(p => p.id);
    const newEditingPrecios = { ...editingPrecios };
    const newEditingValues = { ...editingValues };
    
    Object.keys(editingPrecios).forEach(id => {
      const precioId = parseInt(id);
      if (!selectedIds.includes(precioId)) {
        delete newEditingPrecios[precioId];
        delete newEditingValues[precioId];
      }
    });
    
    setEditingPrecios(newEditingPrecios);
    setEditingValues(newEditingValues);
  };

  // Lógica de paginación
  const totalPages = Math.ceil(filteredPrecios.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPrecios = filteredPrecios.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Limpiar estados de edición cuando cambien las selecciones
  useEffect(() => {
    cleanupEditingStates();
  }, [selectedPrecios]);

  // Resetear a la primera página y limpiar todas las selecciones cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
    // Limpiar todas las selecciones cuando se aplica cualquier filtro
    setSelectedPrecios([]);
    // También limpiar estados de edición
    setEditingPrecios({});
    setEditingValues({});
  }, [filters]);

  // Estados para edición inline
  const [editingPrecios, setEditingPrecios] = useState<{ [key: number]: boolean }>({});
  const [editingValues, setEditingValues] = useState<{ [key: number]: string }>({});
  const [savingPrecios, setSavingPrecios] = useState<{ [key: number]: boolean }>({});

  const columns = [
    { key: "select", text: "✓", align: "center" },
    { key: "color_name", text: "Color", align: "left" },
    { key: "papel_name", text: "Papel", align: "left" },
    { key: "hoja_name", text: "Tamaño", align: "center" },
    { key: "faz_name", text: "Faz", align: "center" },
    { key: "precio", text: "Precio", align: "right" },
  ];

  const handleStartEdit = (precio: PrecioCopia) => {
    setEditingPrecios(prev => ({ ...prev, [precio.id]: true }));
    setEditingValues(prev => ({ ...prev, [precio.id]: precio.precio.toString() }));
  };

  const handleCancelEdit = (precioId: number) => {
    setEditingPrecios(prev => ({ ...prev, [precioId]: false }));
    setEditingValues(prev => {
      const newValues = { ...prev };
      delete newValues[precioId];
      return newValues;
    });
  };

  const handleSaveEdit = async (precio: PrecioCopia) => {
    const newPrice = parseFloat(editingValues[precio.id]);
    
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error('El precio debe ser un número válido no negativo');
      return;
    }

    if (newPrice === precio.precio) {
      handleCancelEdit(precio.id);
      return;
    }

    setSavingPrecios(prev => ({ ...prev, [precio.id]: true }));
    
    try {
      await handleSavePrice(precio.id, newPrice);
      setEditingPrecios(prev => ({ ...prev, [precio.id]: false }));
      setEditingValues(prev => {
        const newValues = { ...prev };
        delete newValues[precio.id];
        return newValues;
      });
      toast.success(`Precio actualizado: ${formatPrice(precio.precio)} → ${formatPrice(newPrice)}`);
    } catch (error) {
      console.error('Error al guardar precio:', error);
    } finally {
      setSavingPrecios(prev => ({ ...prev, [precio.id]: false }));
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent, precio: PrecioCopia) => {
    if (event.key === 'Enter') {
      handleSaveEdit(precio);
    } else if (event.key === 'Escape') {
      handleCancelEdit(precio.id);
    }
  };

  const formatTableRows = () => {
    return currentPrecios.map((precio) => {
      const isEditing = editingPrecios[precio.id];
      const isSaving = savingPrecios[precio.id];
      const isSelected = selectedPrecios.some(p => p.id === precio.id);
      
      return {
        ...precio,
        select: (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => handlePrecioSelection(precio, e.target.checked)}
            style={{ width: '16px', height: '16px' }}
          />
        ),
        precio: isEditing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="number"
              value={editingValues[precio.id] || ''}
              onChange={(e) => setEditingValues(prev => ({ 
                ...prev, 
                [precio.id]: e.target.value 
              }))}
              onKeyDown={(e) => handleKeyPress(e, precio)}
              style={{
                width: '80px',
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              step="0.01"
              min="0"
              autoFocus
            />
            <button
              onClick={() => handleSaveEdit(precio)}
              disabled={isSaving}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {isSaving ? '💾' : '✓'}
            </button>
            <button
              onClick={() => handleCancelEdit(precio.id)}
              disabled={isSaving}
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{formatPrice(precio.precio)}</span>
            <button
              onClick={() => handleStartEdit(precio)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#FE7C40'
              }}
            >
              ✏️
            </button>
          </div>
        ),
                 hoja_name: `${precio.hoja_name} (${formatDimensions(precio.hoja_width, precio.hoja_height)})`,
         papel_name: `${precio.papel_name} (${precio.papel_weight}g)`
      };
    });
  };

  // Componente de paginación
  const Pagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          <Text>
            {`Mostrando ${startIndex + 1}-${Math.min(endIndex, filteredPrecios.length)} de ${filteredPrecios.length} precios`}
          </Text>
        </div>
        
        <div className="pagination-controls">
          <Button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            outlined
            small
          >
            ⏮️ Primera
          </Button>
          
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            outlined
            small
          >
            ⬅️ Anterior
          </Button>
          
          {pageNumbers.map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              style={currentPage === page ? { 
                backgroundColor: '#FE7C40', 
                color: 'white', 
                borderColor: '#FE7C40' 
              } : {}}
              outlined={currentPage !== page}
              small
            >
              {page.toString()}
            </Button>
          ))}
          
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            outlined
            small
          >
            Siguiente ➡️
          </Button>
          
          <Button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            outlined
            small
          >
            Última ⏭️
          </Button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className={isDesktopOrLaptop ? "admin-update-prices-desktop" : "admin-update-prices-mobile"}>
        <LogoSection showInstructions={false} />
        <div className="main-column">
          <div className="error-section">
            <Title>Error al cargar los precios</Title>
            <Text>{error}</Text>
            <Button onClick={handleRefresh} outlined>
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        isDesktopOrLaptop ? "admin-update-prices-desktop" : "admin-update-prices-mobile"
      }
    >
      <LogoSection showInstructions={false} />

      <div className="main-column">
        <div className="top-content">
          <div className="title-section">
            <div className="title-header">
              <Button 
                onClick={handleGoBack}
                outlined
                small
                style={{ marginRight: '16px' }}
              >
                ← Volver
              </Button>
              <Title>Actualizar Precios</Title>
            </div>
            <p>Usuario logueado: {email}</p>
          </div>

          <div className="content-section">
            {/* Filtros */}
            <PriceFilters
              precios={precios}
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={handleClearFilters}
            />

            <div className="prices-header">
              <div className="header-info">
                <h3>Precios de Copias</h3>
                <Text>Configuración de precios para los servicios de impresión</Text>
              </div>
              <div className="header-actions">
                {selectedPrecios.length > 0 && (
                  <Button 
                    onClick={() => setIsBulkEditModalOpen(true)}
                    small
                    style={{ marginRight: '8px' }}
                  >
                    {`✏️ Editar ${selectedPrecios.length} precios`}
                  </Button>
                )}
                <Button onClick={handleRefresh} outlined small>
                  🔄 Actualizar
                </Button>
              </div>
            </div>

            {/* Controles de selección */}
            <div className="selection-controls">
              <div className="selection-info">
                <Text>
                  {selectedPrecios.length > 0 
                    ? `${selectedPrecios.length} de ${filteredPrecios.length} precios seleccionados`
                    : `${filteredPrecios.length} precios disponibles`
                  }
                                     {Object.keys(editingPrecios).length > 0 && (
                     <span style={{ marginLeft: '12px', color: '#FE7C40', fontWeight: 'bold' }}>
                       • {Object.keys(editingPrecios).length} en edición
                                               {hasRealChanges() && (
                          <span style={{ color: '#28a745' }}>
                            {' '}({currentPrecios.filter(precio => {
                              const newPrice = parseFloat(editingValues[precio.id]);
                              return editingPrecios[precio.id] && !isNaN(newPrice) && newPrice >= 0 && newPrice !== precio.precio;
                            }).length} con cambios)
                          </span>
                        )}
                     </span>
                   )}
                </Text>
              </div>
              <div className="selection-buttons">
                                 {selectedPrecios.length > 0 && (
                   <Button 
                     onClick={handleEditSelected}
                     outlined
                     style={{ marginRight: '8px', height: '36px', padding: '8px 12px', fontSize: '13px' }}
                   >
                     ✏️ Editar
                   </Button>
                 )}
                 {Object.keys(editingPrecios).length > 0 && hasRealChanges() && (
                   <Button 
                     onClick={handleSaveAllEditing}
                     loading={bulkUpdating}
                     style={{ marginRight: '8px', height: '36px', padding: '8px 12px', fontSize: '13px' }}
                   >
                     💾 Guardar
                   </Button>
                 )}
                                                     <Button 
                   onClick={handleSelectAll}
                   outlined
                   disabled={selectedPrecios.length === filteredPrecios.length}
                   style={{ 
                     height: '36px', 
                     padding: '8px 12px', 
                     fontSize: '13px',
                     ...(selectedPrecios.length === filteredPrecios.length && filteredPrecios.length > 0 ? {
                       backgroundColor: '#FE7C40',
                       color: 'white',
                       borderColor: '#FE7C40'
                     } : {})
                   }}
                 >
                   ☑️ Todos
                 </Button>
                 <Button 
                   onClick={handleDeselectAll}
                   outlined
                   disabled={selectedPrecios.length === 0}
                   style={{ 
                     height: '36px', 
                     padding: '8px 12px', 
                     fontSize: '13px',
                     ...(selectedPrecios.length === 0 ? {
                       opacity: 0.5
                     } : {})
                   }}
                 >
                   ☐ Ninguno
                 </Button>
              </div>
            </div>

            <div className="table-container">
              {loading ? (
                <div className="loading-container">
                  <LoadingSpinner width={80} height={80} />
                  <Text>Cargando precios...</Text>
                </div>
              ) : (
                <>
                  <Table
                    columns={columns}
                    rows={formatTableRows()}
                    loading={false}
                    onActionUpdate={handleEditPrice}
                  />
                  {totalPages > 1 && <Pagination />}
                </>
              )}
            </div>

            <div className="stats-section">
              <div className="stat-card">
                <Text bold>Total de Precios</Text>
                <Title>{filteredPrecios.length}</Title>
              </div>
              <div className="stat-card">
                <Text bold>Precio Mínimo</Text>
                <Title>{filteredPrecios.length > 0 ? formatPrice(Math.min(...filteredPrecios.map(p => p.precio))) : '$0'}</Title>
              </div>
              <div className="stat-card">
                <Text bold>Precio Máximo</Text>
                <Title>{filteredPrecios.length > 0 ? formatPrice(Math.max(...filteredPrecios.map(p => p.precio))) : '$0'}</Title>
              </div>
              <div className="stat-card">
                <Text bold>Seleccionados</Text>
                <Title>{selectedPrecios.length}</Title>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de edición de precios */}
      <EditPriceModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        precio={selectedPrecio}
        onSave={handleSavePrice}
        loading={updatingPrice}
      />

      {/* Modal de edición masiva */}
      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        selectedPrecios={selectedPrecios}
        onSave={handleBulkEdit}
        loading={bulkUpdating}
      />
    </div>
  );
};
